<script setup lang="ts">
  import { computed, onBeforeMount, ref } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import type { User } from '@stuut/api/models';
  import type { AvatarVariants } from '@ui/components/avatar';
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';

  import { UserAvatar } from '@/components/users';
  import AssigneeView from '@/components/users/AssigneeView.vue';
  import { useProfile, useUsers } from '@/store';
  import { users as userUtils } from '@/utils';

  interface Props {
    assignee: User | null;
    assigneeId?: string;
    disabled?: boolean;
    showAvatar?: boolean;
    showAvatarOnly?: boolean;
    showDropdownArrow?: boolean;
    size?: AvatarVariants['size'];
    triggerClass?: HTMLAttributes['class'];
  }

  defineProps<Props>();

  const emit = defineEmits<{
    (e: 'selected', user: User | null): void;
  }>();

  const open = ref(false);
  const isLoadingUsers = ref(true);
  const isUpdating = ref(false);

  const profileStore = useProfile();
  const userStore = useUsers();

  const canAssign = computed(() => {
    return profileStore.profile?.is_org_admin;
  });

  const users = computed(() => userStore.users);

  const userGroups = computed(() => {
    return userUtils.getUserGroups(users.value);
  });

  onBeforeMount(() => {
    if (!users.value?.length) {
      userStore
        .fetchUsers()
        .catch((error) => {
          toast.error(error.clientMessage);
        })
        .finally(() => {
          isLoadingUsers.value = false;
        });
    } else {
      isLoadingUsers.value = false;
    }
  });
</script>

<template>
  <AssigneeView v-if="!canAssign || disabled" :assignee :show-avatar-only :show-avatar :size />

  <Popover v-else v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        :class="triggerClass"
        variant="ghost"
        size="sm"
        role="combobox"
        :aria-expanded="open"
        :loading="isUpdating || isLoadingUsers"
      >
        <AssigneeView :assignee :show-avatar-only :show-avatar :size />

        <div v-if="!showAvatarOnly || showDropdownArrow" class="text-xs">
          <i class="fal fa-chevron-down text-2xs!" />
        </div>
      </Button>
    </PopoverTrigger>

    <PopoverContent class="w-[250px] p-0" align="end">
      <Command
        :filter-function="
          (list, term) => {
            term = term.toLowerCase();
            return (list as Record<string, any>[]).filter((i) =>
              i.name?.toLowerCase()?.includes(term),
            );
          }
        "
      >
        <CommandList>
          <div class="relative">
            <CommandInput placeholder="Search user..." />
          </div>
          <CommandEmpty>No users found.</CommandEmpty>
          <div class="h-[200px] overflow-y-auto">
            <CommandGroup v-if="assignee">
              <CommandItem
                value="Remove assignee"
                class="flex justify-between text-xs"
                @select="
                  () => {
                    emit('selected', null);
                    open = false;
                  }
                "
              >
                <div class="flex items-center space-x-2">
                  <UserAvatar size="sm" />
                  <p>Remove Assignment</p>
                </div>
              </CommandItem>
            </CommandGroup>

            <CommandGroup v-for="group in userGroups" :key="group.label" :heading="group.label">
              <CommandItem
                v-for="user in group.users"
                :key="user.id"
                :value="user"
                class="flex justify-between text-xs"
                @select="
                  () => {
                    emit('selected', user);
                    open = false;
                  }
                "
              >
                <div class="flex items-center space-x-2">
                  <UserAvatar :user="user" size="sm" />
                  <p>{{ user.name }}</p>
                </div>
              </CommandItem>
            </CommandGroup>
          </div>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<style scoped></style>
